/**
 * Band utilities — frontend mirror of backend BandService.
 *
 * All threshold logic lives here so every component uses consistent
 * band labels, colors, and accessibility tokens. NIST-aligned vocabulary:
 * Low / Moderate / High / Critical.
 *
 * Color-blind-safe: every band has a distinct shape icon, label, and
 * sufficient contrast. Never rely on color alone.
 */

// ── Types ─────────────────────────────────────────────────────────────

export type RiskBandKey = 'low' | 'moderate' | 'high' | 'critical';
export type ReadinessBandKey = 'critical' | 'low' | 'moderate' | 'high' | 'advanced';
export type MaturityBandKey = 'none' | 'foundational' | 'developing' | 'established' | 'advanced';

export interface BandStyle {
  label: string;
  description: string;
  /** CSS custom-property value (works in both light and dark themes) */
  color: string;
  /** Accessible shape indicator character */
  icon: string;
  /** Tailwind-compatible classes for glassmorphism cards */
  bg: string;
  text: string;
  border: string;
  stroke: string;
}

// ── Risk bands (0-100, higher = worse) ────────────────────────────────

const RISK_BAND_STYLES: Record<RiskBandKey, BandStyle> = {
  low: {
    label: 'Low',
    description: 'Within tolerance',
    color: '#10b981',
    icon: '◆',
    bg: 'rgba(16, 185, 129, 0.10)',
    text: '#10b981',
    border: 'rgba(16, 185, 129, 0.30)',
    stroke: '#10b981',
  },
  moderate: {
    label: 'Moderate',
    description: 'Requires monitoring',
    color: '#f59e0b',
    icon: '▲',
    bg: 'rgba(245, 158, 11, 0.10)',
    text: '#f59e0b',
    border: 'rgba(245, 158, 11, 0.30)',
    stroke: '#f59e0b',
  },
  high: {
    label: 'High',
    description: 'Needs attention',
    color: '#f97316',
    icon: '■',
    bg: 'rgba(249, 115, 22, 0.10)',
    text: '#f97316',
    border: 'rgba(249, 115, 22, 0.30)',
    stroke: '#f97316',
  },
  critical: {
    label: 'Critical',
    description: 'Requires immediate action',
    color: '#ef4444',
    icon: '●',
    bg: 'rgba(239, 68, 68, 0.10)',
    text: '#ef4444',
    border: 'rgba(239, 68, 68, 0.30)',
    stroke: '#ef4444',
  },
};

// ── Readiness bands (0-100, higher = better) ──────────────────────────

const READINESS_BAND_STYLES: Record<ReadinessBandKey, BandStyle> = {
  critical: {
    label: 'Critical',
    description: 'Foundational gaps',
    color: '#ef4444',
    icon: '●',
    bg: 'rgba(239, 68, 68, 0.10)',
    text: '#ef4444',
    border: 'rgba(239, 68, 68, 0.30)',
    stroke: '#ef4444',
  },
  low: {
    label: 'Low',
    description: 'Below target',
    color: '#f97316',
    icon: '■',
    bg: 'rgba(249, 115, 22, 0.10)',
    text: '#f97316',
    border: 'rgba(249, 115, 22, 0.30)',
    stroke: '#f97316',
  },
  moderate: {
    label: 'Moderate',
    description: 'Approaching target',
    color: '#f59e0b',
    icon: '▲',
    bg: 'rgba(245, 158, 11, 0.10)',
    text: '#f59e0b',
    border: 'rgba(245, 158, 11, 0.30)',
    stroke: '#f59e0b',
  },
  high: {
    label: 'Established',
    description: 'On track',
    color: '#10b981',
    icon: '◆',
    bg: 'rgba(16, 185, 129, 0.10)',
    text: '#10b981',
    border: 'rgba(16, 185, 129, 0.30)',
    stroke: '#10b981',
  },
  advanced: {
    label: 'Advanced',
    description: 'Best-in-class',
    color: '#06b6d4',
    icon: '★',
    bg: 'rgba(6, 182, 212, 0.10)',
    text: '#06b6d4',
    border: 'rgba(6, 182, 212, 0.30)',
    stroke: '#06b6d4',
  },
};

// ── Core mapping functions ────────────────────────────────────────────

export function getRiskBand(score: number): RiskBandKey {
  const s = Math.round(score || 0);
  if (s <= 30) return 'low';
  if (s <= 55) return 'moderate';
  if (s <= 75) return 'high';
  return 'critical';
}

export function getRiskBandStyle(score: number): BandStyle {
  return RISK_BAND_STYLES[getRiskBand(score)];
}

export function getRiskBandLabel(score: number): string {
  return RISK_BAND_STYLES[getRiskBand(score)].label;
}

export function getReadinessBand(score: number): ReadinessBandKey {
  const s = Math.round(score || 0);
  if (s <= 25) return 'critical';
  if (s <= 45) return 'low';
  if (s <= 65) return 'moderate';
  if (s <= 85) return 'high';
  return 'advanced';
}

export function getReadinessBandStyle(score: number): BandStyle {
  return READINESS_BAND_STYLES[getReadinessBand(score)];
}

export function getReadinessBandLabel(score: number): string {
  return READINESS_BAND_STYLES[getReadinessBand(score)].label;
}

export function getMaturityBand(score: number): MaturityBandKey {
  const s = score || 0;
  if (s < 0.5) return 'none';
  if (s < 1.5) return 'foundational';
  if (s < 2.5) return 'developing';
  if (s < 3.5) return 'established';
  return 'advanced';
}

const MATURITY_LABELS: Record<MaturityBandKey, string> = {
  none: 'Not Assessed',
  foundational: 'Foundational',
  developing: 'Developing',
  established: 'Established',
  advanced: 'Advanced',
};

export function getMaturityBandLabel(score: number): string {
  return MATURITY_LABELS[getMaturityBand(score)];
}

// ── CRS band (same thresholds as risk, but historically had different names) ──

/** Legacy CRS band key → NIST label */
export function legacyCrsBandToLabel(legacyBand: string): string {
  const map: Record<string, string> = {
    stable: 'Low',
    elevated: 'Moderate',
    high: 'High',
    critical: 'Critical',
  };
  return map[legacyBand] || legacyBand;
}

/** Legacy CRS band key → new band key for style lookup */
export function legacyCrsBandToKey(legacyBand: string): RiskBandKey {
  const map: Record<string, RiskBandKey> = {
    stable: 'low',
    elevated: 'moderate',
    high: 'high',
    critical: 'critical',
  };
  return map[legacyBand] || 'moderate';
}

export function getCrsBandStyle(bandOrScore: string | number): BandStyle {
  if (typeof bandOrScore === 'string') {
    return RISK_BAND_STYLES[legacyCrsBandToKey(bandOrScore)];
  }
  return RISK_BAND_STYLES[getRiskBand(bandOrScore)];
}

// ── Peer comparison helpers ───────────────────────────────────────────

export function getQuartileLabel(percentile: number): string {
  const p = percentile || 50;
  if (p >= 75) return 'Top quartile';
  if (p >= 50) return 'Above median';
  if (p >= 25) return 'Below median';
  return 'Bottom quartile';
}

export function getRelativeComparison(ourScore: number, peerAverage: number): string {
  const delta = ourScore - peerAverage;
  const absDelta = Math.abs(delta);
  if (absDelta < 5) return 'in line with';
  if (delta > 0 && absDelta >= 20) return 'significantly above';
  if (delta > 0) return 'above';
  if (delta < 0 && absDelta >= 20) return 'significantly below';
  return 'below';
}

// ── Goldilocks helpers ────────────────────────────────────────────────

export function formatOptimizationAction(currentScore: number, targetScore: number): string {
  const currentBand = getReadinessBandLabel(currentScore);
  const targetBand = getReadinessBandLabel(targetScore);
  if (currentBand === targetBand) return `Maintain at ${currentBand}`;
  return `Improve from ${currentBand} to ${targetBand}`;
}

export function getOptimalRangeLabel(optimalMin: number, optimalMax: number): string {
  return `${getReadinessBandLabel(optimalMin)} to ${getReadinessBandLabel(optimalMax)}`;
}

// ── Portfolio aggregation helpers (Portfolio Manager view) ───────────

export interface PortfolioDistribution {
  critical: number;
  high: number;
  moderate: number;
  low: number;
  total: number;
}

/**
 * Given an array of per-client risk scores, return the count of clients in
 * each band. Used on the PM dashboard headline KPI in place of a single
 * "average risk score" — averages across mixed bands don't translate cleanly.
 * Null/undefined scores are skipped (not counted in `total`).
 */
export function portfolioDistribution(scores: Array<number | null | undefined>): PortfolioDistribution {
  const dist: PortfolioDistribution = { critical: 0, high: 0, moderate: 0, low: 0, total: 0 };
  for (const s of scores) {
    if (s === null || s === undefined || Number.isNaN(s)) continue;
    dist.total++;
    dist[getRiskBand(s)]++;
  }
  return dist;
}

// ── Band-trend shorthand (PM-safe trend deltas) ───────────────────────

export type BandTrendDirection = 'up' | 'down' | 'flat';

export interface BandTrend {
  direction: BandTrendDirection;
  oldBand: RiskBandKey;
  newBand: RiskBandKey;
  /** Short PM-facing label, e.g. "Moved into High", "Dropped to Moderate", "No change". */
  label: string;
  /** Arrow glyph for inline rendering. */
  arrow: '↑' | '↓' | '→';
}

/**
 * Convert a (oldScore, newScore) pair into a PM-safe band-change shorthand.
 * No raw numeric delta — only the band movement is exposed to the PM, per
 * the band-scrub principle: "squishy numbers become bands, hard numbers stay."
 *
 * "Up" means risk got worse (band climbed); "down" means risk improved.
 */
export function bandTrend(oldScore: number, newScore: number): BandTrend {
  const oldBand = getRiskBand(oldScore);
  const newBand = getRiskBand(newScore);
  const order: Record<RiskBandKey, number> = { low: 0, moderate: 1, high: 2, critical: 3 };
  const delta = order[newBand] - order[oldBand];
  const newLabel = RISK_BAND_STYLES[newBand].label;
  if (delta > 0) {
    return { direction: 'up', oldBand, newBand, label: `Moved into ${newLabel}`, arrow: '↑' };
  }
  if (delta < 0) {
    return { direction: 'down', oldBand, newBand, label: `Dropped to ${newLabel}`, arrow: '↓' };
  }
  return { direction: 'flat', oldBand, newBand, label: 'No change', arrow: '→' };
}
